<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useDataStore } from '@/stores/useDataStore';
import { useHistorycalStore } from '@/stores/useHistorycalStore';
import { useReactorStateStore } from '@/stores/useReactorStateStore';
import { idMapping, getZoneLabel } from '@/functions/utils';
import { isMainSpecie } from '@/functions/functions_cultures';
import { getThresholdsClass } from '@/functions/functions_pilotage';

// Import icons
import parasitesIcon from '@/assets/parasites.png';
import chlorellaIcon from '@/assets/chlorella.png';
import cystIcon from '@/assets/cyst.png';
import ciliatesIcon from '@/assets/ciliates.png';
import smpIcon from '@/assets/smp.png';
import amoebaIcon from '@/assets/amoeba.png';
import fungiIcon from '@/assets/fungi.png';
import sporangesIcon from '@/assets/sporanges.png';
import clumpIcon from '@/assets/clump.png';
import ovoidCellsIcon from '@/assets/ovoidCells.png';

const dataStore = useDataStore();
const historyStore = useHistorycalStore();
const reactorStateStore = useReactorStateStore();
const error = ref(null);

// Mapping of observation types to icons (keyed by DB name)
const observationIcons: Record<string, string> = {
  'Chromovores':   parasitesIcon,
  'Flagellites':   fungiIcon,
  'Nanoplasts':    smpIcon,
  'Viridocysts':   cystIcon,
  'Sporulants':    ciliatesIcon,
  'Phycoplaques':  amoebaIcon,
  'Zoïdocysts':    chlorellaIcon,
  'Trichonodes':   sporangesIcon,
  'Endoplastes':   clumpIcon,
  'Rhizoflagelles': ovoidCellsIcon,
};

// Filtrer les PBR pour exclure MC1-MC8 et IBC
const filteredPbrs = computed(() => {
  return dataStore.pbrs.filter(pbr => !pbr.numero_pbr.startsWith('MC') && pbr.numero_pbr !== 'IBC');
});

// Créer une liste combinée avec les états, les PBR, les cultures, la productivité et les observations
const combinedData = computed(() => {
  return filteredPbrs.value
    .filter(pbr => pbr.zone != null && pbr.numero_pbr.startsWith('B'))
    .map(pbr => {
      const reactor = historyStore.reactorsState.find(
        r => idMapping[r.id] === pbr.id
      );
      const culture = dataStore.cultures.find(
        c => c.pbr.id === pbr.id && c.active && c.specie?.specie_code
      );
      const latestHarvest = reactorStateStore.filteredHarvestYield
        .filter(hy => hy.pbr === pbr.numero_pbr)
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0];

      let state = reactor
        ? (reactor.state ? reactor.state.charAt(0).toUpperCase() + reactor.state.slice(1).toLowerCase() : 'Inconnu')
        : 'Inconnu';
      let observations: { id: number; name: string; value: number }[] = [];

      if (culture) {
        const latestRecolte = dataStore.recoltes
          .filter(r => r.cultures?.some(c => c.pbr.id === pbr.id && c.active))
          .sort((a, b) => {
            const dateA = new Date(a.daycode);
            const dateB = new Date(b.daycode);
            return dateB.getTime() - dateA.getTime();
          })[0];
        if (latestRecolte) {
          const activeCultureInRecolte = latestRecolte.cultures?.find(c => c.pbr.id === pbr.id && c.active);
          if (activeCultureInRecolte) {
            state = 'Production';
            if (activeCultureInRecolte.prelevement?.observation_values) {
              observations = activeCultureInRecolte.prelevement.observation_values
                .filter(
                  (obs): obs is Observation & { observation_type: ObservationType; value: number } =>
                    obs.observation_type_id != null &&
                    obs.observation_type != null &&
                    obs.observation_type.name != null &&
                    obs.value != null &&
                    obs.value !== 0
                )
                .map(obs => ({
                  id: obs.observation_type_id,
                  name: obs.observation_type.name,
                  value: obs.value
                }));
            }
          } else {
            state = 'Growing';
          }
        } else {
          state = 'Growing';
        }
      }

      const isMain = culture ? isMainSpecie(culture.specie.id) : false;
      const productivity = isMain && latestHarvest?.productiviteBsMain
        ? latestHarvest.productiviteBsMain
        : latestHarvest?.productiviteBs ?? null;

      return {
        id: pbr.id,
        numero_pbr: pbr.numero_pbr,
        zone: pbr.zone as string,
        state,
        specie_code: culture?.specie.specie_code ?? '',
        specieId: culture?.specie.id ?? 0,
        productivity: productivity,
        observations
      };
    });
});

// Grouper les PBR par zone
const groupedByZone = computed(() => {
  const groups: Record<string, { pbrs: typeof combinedData.value; occupancyRate: number; zone: string }> = {};

  // Initialiser les groupes par zone
  combinedData.value.forEach(item => {
    const zone = item.zone;

    if (!groups[zone]) {
      groups[zone] = { zone, pbrs: [], occupancyRate: 0 };
    }
    groups[zone].pbrs.push(item);
  });

  // Calculer le taux d'occupation pour chaque zone
  Object.keys(groups).forEach(zone => {
    const pbrs = groups[zone].pbrs;
    const totalPbrs = pbrs.length;
    const operationalPbrs = pbrs.filter(
      pbr => pbr.state === 'Growing' || pbr.state === 'Production'
    ).length;
    groups[zone].occupancyRate = totalPbrs > 0 ? (operationalPbrs / totalPbrs) * 100 : 0;
  });

  return groups;
});

// Liste des observations pour la légende
const observationLegendItems = [
  { name: 'Chromovores',    icon: parasitesIcon },
  { name: 'Flagellites',    icon: fungiIcon },
  { name: 'Nanoplasts',     icon: smpIcon },
  { name: 'Viridocysts',    icon: cystIcon },
  { name: 'Sporulants',     icon: ciliatesIcon },
  { name: 'Phycoplaques',   icon: amoebaIcon },
  { name: 'Zoïdocysts',     icon: chlorellaIcon },
  { name: 'Trichonodes',    icon: sporangesIcon },
  { name: 'Endoplastes',    icon: clumpIcon },
  { name: 'Rhizoflagelles', icon: ovoidCellsIcon },
];

function getPbrClass(pbr: any): string {
  if (pbr.state === 'Interproduction') return 'interproduction';
  if (pbr.state === 'Stopped') return 'stopped';
  if (pbr.state === 'Growing') return 'growing';
  return getThresholdsClass('ProdMs', pbr.specieId, pbr.productivity) || 'default';
}



onMounted(() => {
  dataStore.filtreAnnee = new Date().getFullYear();
  historyStore.fetchState();
  reactorStateStore.loadData();
});
</script>

<template>
  <div>
    <div v-if="reactorStateStore.isLoading" class="spinner-container">
      <div class="spinner"></div>
      <p>{{ $t('loadingData') }}</p>
    </div>
    <div v-else>
      <!-- Légende -->
      <div class="module color-legend">
        <h4>{{ $t('legendTitle') }}</h4>
        <div class="legend-section">
          <div class="legend-items">
            <div v-for="item in observationLegendItems" class="legend-item" :key="item.name">
              <img :src="item.icon" class="legend-icon" :alt="item.name" />
              <span>{{ item.name }}</span>
            </div>
          </div>
        </div>

        <div class="legend-section">
          <div class="legend-items">
            <div class="legend-item">
              <span class="color-box" style="background-color: darkcyan;"></span> {{ $t('growing') }}
              <span class="color-box prod-pink" style="margin-left: 15px;"></span> {{ $t('legend.item5') }}
              <span class="color-box prod-orange" style="margin-left: 15px;"></span> {{ $t('legend.item6') }}
              <span class="color-box prod-green" style="margin-left: 15px;"></span> {{ $t('legend.item7') }}
              <span class="color-box" style="background-color: #a6a6a6; margin-left: 15px;"></span> {{
                $t('interproduction') }}
              <span class="color-box" style="background-color: black; margin-left: 15px;"></span> {{ $t('stopped') }}
            </div>
          </div>
        </div>
      </div>

      <!-- Message d'erreur -->
      <div v-if="error" class="error-message">
        {{ error }}
      </div>

      <!-- Affichage des zones -->
      <div v-else class="zones">
        <div v-for="(zoneData, zone) in groupedByZone" class="module" :key="zone">
          <h3>{{ getZoneLabel(zone as 'zoneA' | 'zoneB' | 'zoneC') }} ({{ $t('occupancyRate') }}: {{ zoneData.occupancyRate.toFixed(2) }}%)</h3>
          <div class="pbr-container">
            <div v-for="pbr in zoneData.pbrs" class="pbr-wrapper" :key="pbr.id">
              <div class="pbr" :class="getPbrClass(pbr)">
                <span class="pbr-specie">{{ pbr.specie_code }}</span>
              </div>
              <span class="pbr-name">{{ pbr.numero_pbr }}</span>
              <span v-if="pbr.productivity" class="pbr-productivity">
                {{ pbr.productivity.toFixed(2) }} kg/m³/y
              </span>
              <div class="pbr-observations">
                <img v-for="obs in pbr.observations" :src="observationIcons[obs.name]" class="observation-icon"
                  :alt="obs.name" :key="obs.id" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.sub-zone-block {
  margin-left: 20px;
  padding: 10px;
}

.stopped {
  background-color: black;
}

.growing {
  background-color: darkcyan;
  color: white;
  font-weight: bold;
}

.default {
  background-color: inherit;
  color: black;
  font-weight: bold;
  border: 2px solid darkgrey;
}

.interproduction {
  background-color: #a6a6a6;
}
</style>